import { keepPreviousData } from '@tanstack/react-query';
import type { BenchmarkScoring, GetProductCategoriesQueryVariables, ProductCategoryView } from '@vizit/api';
import { useGetProductCategoriesQuery } from '@vizit/api';
import type { PickerPathsStatus, PickerProductCategory } from '@vizit/organisms';
import * as React from 'react';

/**
 * One page of search results, sorted by name. Organizations can have thousands; the API sets no maximum. A few letters
 * keep a search well under this.
 */
const PRODUCT_CATEGORIES_LIMIT = 500;

/**
 * A category as the picker and the add dialog show it. `breadcrumbs` holds the categories above it, outermost first
 * and comma separated, without its own name: Cats under Pet Supplies arrives as "Pet Supplies", a top level one as "".
 */
function toPickerProductCategory(category: ProductCategoryView): PickerProductCategory {
  return {
    id: category.id,
    name: category.name,
    color: category.color_label_bg,
    parents: category.breadcrumbs ? category.breadcrumbs.split(',') : [],
    retailer: category.retailer_name ?? null,
  };
}

/** The most IDs one read of the list route takes; it refuses more. */
const MAX_CATEGORY_IDS = 100; // score-threshold-allow: the list route's cap on ids, not a score

interface UseScoredProductCategoriesProps {
  workspaceId?: string;
  /** The scorings whose categories to read, e.g. the ones the picker lists */
  scorings: Pick<BenchmarkScoring, 'productCategoryId'>[];
  /** Reads only while true */
  enabled?: boolean;
}

/**
 * The categories a page's scorings name, as the picker shows them, read in one go on the list route by ID, plus a
 * lookup by ID for naming and coloring the scorings. The IDs are sorted, so selecting a category, which reorders the
 * scorings, reads nothing again; while a new set reads, the last one stands in. A category the read does not return,
 * e.g. since deleted, is not in the lookup. `status` is `error` when the read failed, so the picker can say why rows
 * have no path rather than look like they have none.
 */
function useScoredProductCategories({ workspaceId, scorings, enabled = true }: UseScoredProductCategoriesProps) {
  // Past the cap the list route refuses the read; a benchmark is not scored under that many categories.
  const ids = React.useMemo(
    () => [...new Set(scorings.map((scoring) => scoring.productCategoryId))].sort().slice(0, MAX_CATEGORY_IDS),
    [scorings],
  );
  const { data, isLoading, isPlaceholderData, isError } = useGetProductCategoriesQuery(
    { workspace_id: workspaceId, ids },
    { enabled: enabled && !!workspaceId && ids.length > 0, placeholderData: keepPreviousData },
  );

  const byId = React.useMemo(
    () => new Map((data?.data ?? []).map((category) => [category.id, toPickerProductCategory(category)])),
    [data],
  );
  const status: PickerPathsStatus = isError ? 'error' : isLoading || isPlaceholderData ? 'loading' : 'ready';

  return { byId, status };
}

/**
 * Whether a previous read had the same variables but the search, compared field by field, so its results may stand in
 * while the new search loads. The variables are the object in the wrapper's query key, wherever it sits.
 */
function isSameButSearch(previousKey: readonly unknown[] | undefined, variables: GetProductCategoriesQueryVariables) {
  const previous = previousKey?.find((part): part is Record<string, unknown> => !!part && typeof part === 'object');
  if (!previous) {
    return false;
  }
  const current: Record<string, unknown> = { ...variables };
  const fields = new Set([...Object.keys(previous), ...Object.keys(current)]);
  return [...fields].every(
    (field) => field === 'name_like' || field === 'breadcrumbs_like' || previous[field] === current[field],
  );
}

/** What the add dialog lists while its filters are not known, one array so its identity is stable. */
const NO_CATEGORIES: PickerProductCategory[] = [];

const NO_FILTERS: Partial<GetProductCategoriesQueryVariables> = {};

interface UseProductCategorySearchProps {
  workspaceId?: string;
  /**
   * More of the route's filters, merged into the one set of query variables, e.g. `scorable_for_benchmark_id` to
   * list only the categories a benchmark can score; `null` while they are not known yet, which reads and lists
   * nothing and reports loading
   */
  filters?: Partial<GetProductCategoriesQueryVariables> | null;
  /** Searches only while true, e.g. while the add dialog is open; turning it off clears the search */
  enabled?: boolean;
}

/**
 * The categories the add dialog lists, searched on the API by name or path as the dialog reports its search text
 * (`setSearch`). Nothing is read until there is a search. The previous list stays up while a new search under the same
 * filters loads, so the rows do not blink out between keystrokes; under other filters, e.g. another benchmark, it
 * would list categories that one cannot score. Paused, it keeps listing what it has, so the dialog does not empty as it
 * closes.
 */
function useProductCategorySearch({
  workspaceId,
  filters = NO_FILTERS,
  enabled = true,
}: UseProductCategorySearchProps) {
  const [search, setSearch] = React.useState('');

  React.useEffect(() => {
    if (!enabled) {
      setSearch('');
    }
  }, [enabled]);

  const term = search.trim() || undefined;
  const variables = React.useMemo<GetProductCategoriesQueryVariables>(
    () => ({
      workspace_id: workspaceId,
      limit: PRODUCT_CATEGORIES_LIMIT,
      sort_by: 'name',
      sort_direction: 'asc',
      // The API matches either, ignoring case, so "pet" finds Cats under Pet Supplies too.
      name_like: term,
      breadcrumbs_like: term,
      ...filters,
    }),
    [workspaceId, term, filters],
  );

  // Without its filters the search would list, or answer from the cache, categories the benchmark may not score.
  const isReady = filters !== null;
  const { data, isLoading, isPlaceholderData, isError, refetch } = useGetProductCategoriesQuery(variables, {
    enabled: enabled && !!workspaceId && isReady && !!term,
    placeholderData: (previous, previousQuery) =>
      isSameButSearch(previousQuery?.queryKey, variables) ? previous : undefined,
  });

  const categories = React.useMemo<PickerProductCategory[]>(
    () => (data?.data ?? []).map(toPickerProductCategory),
    [data],
  );

  return {
    categories: isReady ? categories : NO_CATEGORIES,
    /** How many categories match the search in all; more than `categories` when the API capped the list */
    totalCount: isReady ? data?.total_count : undefined,
    isLoading: isLoading || !workspaceId || (enabled && !isReady),
    /** Whether the list is the previous search's while the current one loads */
    isSearching: isReady && isPlaceholderData,
    /** Whether the list failed to load, e.g. the benchmark of a scorable filter is gone */
    isError: isReady && isError,
    retry: refetch,
    search,
    setSearch,
  };
}

/**
 * Finds a category by ID to name and color a scoring or a refusal: from the lists the add dialog has shown, else from
 * the scorings' categories (`useScoredProductCategories`). A category picked from a search (under this search or an
 * earlier one) is found from the dialog's list before its scoring lists and is read.
 */
function useFindProductCategory(
  byId: ReadonlyMap<string, PickerProductCategory>,
  listed: PickerProductCategory[],
): (categoryId: string) => PickerProductCategory | undefined {
  const [listedById, setListedById] = React.useState<ReadonlyMap<string, PickerProductCategory>>(() => new Map());
  React.useEffect(() => {
    if (listed.length === 0) {
      return;
    }
    // The latest listing wins, so a category renamed since keeps its new name.
    setListedById((previous) => new Map([...previous, ...listed.map((category) => [category.id, category] as const)]));
  }, [listed]);

  return React.useCallback(
    (categoryId: string) => listedById.get(categoryId) ?? byId.get(categoryId),
    [byId, listedById],
  );
}

export { toPickerProductCategory, useFindProductCategory, useProductCategorySearch, useScoredProductCategories };
